import React from 'react'
import {Route, Navigate} from "react-router";
import {withAccessControl} from "../ProtectedRoute";
import {AccountType, SubscriptionType} from "../../components/constants/securspace-constants";
import {getAdminUserTypes, getGateOperatorUserTypes} from "../../util/AccountUtils";
import AccessControlBuilder from "../AccessControlBuilder";

import SupplierInvoicesReport from "../../views/supplier/SupplierInvoicesReport";
import SupplierAccountsReceivableReport from "../../views/invoices/SupplierAccountsReceivableReport";
import SupplierReadyForPayoutReport from "../../views/SupplierReadyForPayoutReport";
import SupplierPayoutsReportLegacy from "../../views/SupplierPayoutsReportLegacy";
import SupplierPayoutsReport from "../../views/payouts/SupplierPayoutsReport";
import GmsUpgrade from "../../components/gms/GmsUpgrade";
import ChargeServiceFee from "../../views/servicefees/ChargeServiceFee";
import PartnerSubscriptions from '../../views/PartnerSubscriptions';
import PartnerSubscriptionInvoicesReport from '../../views/PartnerSubscriptionInvoicesReport';
import PartnerSubscriptionsLegacy from '../../views/PartnerSubscriptionLegacy';
import PartnerSubscriptionInvoicesReportLegacy from '../../views/PartnerSubscriptionInvoicesReportLegacy';
import {useFeatureFlags} from "../../hooks/routing/useFeatureFlags";
import SupplierSpaceUsage from "../../views/SupplierSpaceUsage";
import SupplierInventoryCorrections from '../../views/inventory-correction/SupplierInventoryCorrections';
import SupplierBooking from '../../views/booking-overview/SupplierBooking';
import SupplierBookingApprovals from '../../views/supplier-bookings-approvals/SupplierBookingApprovals';
import SupplierReadyForPayout from "../../views/SupplierReadyForPayout/SupplierReadyForPayout";
import InvoicesInPayoutReport from "../../views/supplier-payout/InvoicesInPayoutReport";
import BrokerBooking from "../../views/broker-booking/BrokerBooking";
import SupplierBookingsReport from "../../views/SupplierBookingsReport";
import ApiIntegration from "../../views/api-integration";

const allSupplierOwnerAuthorization = new AccessControlBuilder().allowAllSupplierOwner().build()
const gmsProGateManagersAuthorization = new AccessControlBuilder().allowGmsProGateManagers().build()
const gateManagerAuthorization = new AccessControlBuilder().allowGateManager().build()
const gmsProOwnerAuthorization = new AccessControlBuilder().allowGmsProOwner().build()

const modifiedGateManagerAuthorization = {
  ...gateManagerAuthorization,
}

const gmsUpgradeAuthorization = new AccessControlBuilder()
  .allowAccountType(AccountType.SUPPLIER)
  .allowUserTypes(getGateOperatorUserTypes())
  .allowSubscriptionType(SubscriptionType.MARKETPLACE_ONLY)
  .build()

const supplierAdminAuthorization = new AccessControlBuilder()
  .allowAccountType(AccountType.SUPPLIER)
  .allowUserTypes(getAdminUserTypes())
  .allowGmsSubscriptionTypes()
  .build();

const allSupplierAdminAuthorization = new AccessControlBuilder()
  .allowAccountType(AccountType.SUPPLIER)
  .allowUserTypes(getAdminUserTypes())
  .allowAllSubscriptionTypes()
  .build();

//Suppliers
const ProtectedSupplierBookingsReport = withAccessControl(SupplierBookingsReport, allSupplierOwnerAuthorization, modifiedGateManagerAuthorization)
const ProtectedRedesignSupplierBooking = withAccessControl(SupplierBooking, allSupplierOwnerAuthorization, modifiedGateManagerAuthorization)
const ProtectedSupplierInvoicesReport = withAccessControl(SupplierInvoicesReport, allSupplierOwnerAuthorization)
const ProtectedSupplierReadyForPayoutReport = withAccessControl(SupplierReadyForPayoutReport, allSupplierOwnerAuthorization)
const ProtectedSupplierPayoutsReportLegacy = withAccessControl(SupplierPayoutsReportLegacy, allSupplierOwnerAuthorization)
const ProtectedSupplierPayoutsReport = withAccessControl(SupplierPayoutsReport, allSupplierOwnerAuthorization)
const ProtectedPartnerSubscriptionsReportLegacy = withAccessControl(PartnerSubscriptionsLegacy, gmsProOwnerAuthorization);
const ProtectedPartnerSubscriptionsReport = withAccessControl(PartnerSubscriptions, gmsProOwnerAuthorization);
const ProtectedPartnerSubscriptionsInvoicesLegacyReport = withAccessControl(PartnerSubscriptionInvoicesReportLegacy, gmsProOwnerAuthorization);
const ProtectedPartnerSubscriptionsInvoicesReport = withAccessControl(PartnerSubscriptionInvoicesReport, gmsProOwnerAuthorization);
const ProtectedSupplierInventoryCorrections = withAccessControl(SupplierInventoryCorrections, gmsProGateManagersAuthorization);
const ProtectedGmsUpgrade = withAccessControl(GmsUpgrade, gmsUpgradeAuthorization)
const ProtectedChargeServiceFee = withAccessControl(ChargeServiceFee, allSupplierOwnerAuthorization)
const ProtectedRedesignSupplierSpaceUsage = withAccessControl(SupplierSpaceUsage, gmsProOwnerAuthorization);
const ProtectedRedesignSupplierBookingApprovals = withAccessControl(SupplierBookingApprovals, allSupplierOwnerAuthorization, gateManagerAuthorization);
const ProtectedRedesignSupplierReadyForPayout = withAccessControl(SupplierReadyForPayout, allSupplierOwnerAuthorization);
const ProtectedInvoicesInPayout = withAccessControl(InvoicesInPayoutReport, allSupplierOwnerAuthorization);

//Supplier Admins
const ProtectedBrokerBooking = withAccessControl(BrokerBooking, supplierAdminAuthorization);
const ProtectedSupplierAccountsReceivable = withAccessControl(SupplierAccountsReceivableReport, allSupplierAdminAuthorization);
const ProtectedApiIntegration = withAccessControl(ApiIntegration, gmsProOwnerAuthorization);

//Supplier Super Admins
// none

const SupplierContent = (): React.ReactElement<any>[] => {
  const {
    createManualBrokeredBooking,
    partnerSubscriptionRedesign,
    supplierReadyForPayoutRedesign,
    supplierBookingsNavRedesign, // This flag is needed so AMB can make bookings
    supplierApiIntegration
  } = useFeatureFlags();

  return [
    <Route path={"/approvals"} key={"/approvals"} element={
      <Navigate to={'/bookings/approvals'}/>
    }/>,
    <Route path={"/supplier-bookings"} key={"/supplier-bookings"} element={
      supplierBookingsNavRedesign ? <Navigate to={'/bookings/supplier-bookings'}/> :
        <ProtectedSupplierBookingsReport/>}
    />,
    <Route path={"/supplier-payouts"} key={"/supplier-payouts"} element={
      supplierReadyForPayoutRedesign ? <Navigate to={"/payouts/supplier-payouts"}/> :
        <ProtectedSupplierPayoutsReportLegacy/>
    }/>,
    <Route path={"/subscriptions"} key={"/subscriptions"} element={
      partnerSubscriptionRedesign ? <Navigate to={"/supplier-subscriptions"}/> :
        <ProtectedPartnerSubscriptionsReportLegacy/>
    }/>,
    <Route path={"/ready-for-payout"} key={"/ready-for-payout"} element={
      supplierReadyForPayoutRedesign ? <ProtectedRedesignSupplierReadyForPayout/> :
        <ProtectedSupplierReadyForPayoutReport/>
    }/>,
    <Route path={"/supplier-booked-space-calendar"} key={"/supplier-booked-space-calendar"} element={
      <Navigate to={'/bookings/space-usage'}/>
    }/>,
    <Route path={"/inventory-corrections"} key={"/inventory-corrections"} element={
      <ProtectedSupplierInventoryCorrections/>
    }/>,
    <Route path={"/supplier-invoices"} key={"/supplier-invoices"} element={
      <Navigate to={'/finances/supplier-invoices'}/>
    }/>,
    <Route path={"/finances/supplier-accounts-receivable"} key={"/supplier-accounts-receivable"} element={
      <ProtectedSupplierAccountsReceivable/>
    }/>,

    <Route path={"/booking/:bookingId/charge-service-fee"} key={"booking/bookingId/charge-service-fee"}
           element={<ProtectedChargeServiceFee/>}/>,
    <Route path={"/bookings/approvals"} key={"/bookings/approvals"}
           element={<ProtectedRedesignSupplierBookingApprovals/>}/>,
    <Route path={"/bookings/space-usage"} key={"/bookings/space-usage"}
           element={<ProtectedRedesignSupplierSpaceUsage/>}/>,
    <Route path={"/bookings/supplier-bookings"} key={"/bookings/supplier-bookings"}
           element={
      <ProtectedRedesignSupplierBooking/>}/>,
    <Route path={"/finances/invoices-in-payout/:payoutCorrelationId"}
           key={"/finances/invoices-in-payout/:payoutCorrelationId"} element={<ProtectedInvoicesInPayout/>}/>,
    <Route path={"/finances/supplier-invoices"} key={"/finances/supplier-invoices"}
           element={<ProtectedSupplierInvoicesReport/>}/>,
    <Route path={"/gms-upgrade"} key={"/gms-upgrade"} element={<ProtectedGmsUpgrade/>}/>,
    <Route path={"/payouts/supplier-payouts"} key={"/payouts/supplier-payouts"}
           element={<ProtectedSupplierPayoutsReport />}/>,
    <Route path={"/subscriptions/:subscriptionId"} key={"/subscriptions/subscriptionId"}
           element={<ProtectedPartnerSubscriptionsInvoicesLegacyReport/>}/>,
    <Route path={"/supplier-subscriptions"} key={"/supplier-subscriptions"}
           element={<ProtectedPartnerSubscriptionsReport/>}/>,
    <Route path={"/supplier-subscriptions/:subscriptionId"} key={"/supplier-subscriptions/subscriptionId"}
           element={<ProtectedPartnerSubscriptionsInvoicesReport/>}/>,
    ...(supplierApiIntegration ? [
      <Route path={"/api-integration"} key={"/api-integration"}
             element={<ProtectedApiIntegration/>}/>
    ] : []),
    <Route path={"/booking/broker"} key={"/booking/broker"} element={
      createManualBrokeredBooking ? <ProtectedBrokerBooking/> : <Navigate to={'/'}/>}/>
  ];
};

export default SupplierContent;
